import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Configure axios for credentials and CSRF
  axios.defaults.withCredentials = true;
  axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
  axios.defaults.headers.common['Accept'] = 'application/json';

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/user');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const getCsrfCookie = async () => {
    // Use the proxy to get CSRF cookie from backend
    await axios.get('/sanctum/csrf-cookie');
  };

  const login = async (email, password) => {
    await getCsrfCookie();
    await axios.post('/api/login', { email, password });
    await checkAuth();
  };

  const register = async (name, email, password, password_confirmation) => {
    await getCsrfCookie();
    await axios.post('/api/register', {
      name,
      email,
      password,
      password_confirmation,
    });
    await checkAuth();
  };

  const logout = async () => {
    await axios.post('/api/logout');
    setUser(null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
